/**
 * Shared money utilities - the single home for monetary rounding,
 * summation, and input parsing.
 *
 * Monetary values in this app are decimal SEK, quantized to whole öre
 * (2 decimals) at every calculation step. Floats only represent
 * öre-quantized values, so exact arithmetic is achieved by working in
 * integer öre internally where it matters (summation).
 */

/** Round to whole öre (2 decimals) - THE rounding policy for money. */
export function roundMoney(sek: number): number {
  return Math.round(sek * 100) / 100
}

/**
 * Exact money summation: quantizes each term to whole öre and
 * accumulates in integer öre, so the result has no floating-point
 * noise (e.g. sumMoney([0.1, 0.2]) === 0.3, exactly).
 */
export function sumMoney(values: number[]): number {
  return values.reduce((acc, v) => acc + Math.round(v * 100), 0) / 100
}

/**
 * Parse a money amount from user or CSV input.
 *
 * Handles Swedish and English formats:
 *   "1 234,56"  -> 1234.56   (space thousands, comma decimal)
 *   "1.234,56"  -> 1234.56   (dot thousands, comma decimal)
 *   "1,234.56"  -> 1234.56   (comma thousands, dot decimal)
 *   "1234.56"   -> 1234.56
 *   "40,50"     -> 40.5
 *   "1,234"     -> 1234      (single comma + 3 digits = thousands)
 *   "12,5"      -> 12.5      (single comma + 1-2 digits = decimal)
 *   "150 kr" / "SEK 150" / "€150" -> 150
 *   "-150,50"   -> -150.5
 *
 * Rules:
 * - A dot is always a decimal separator unless a comma follows it.
 * - With both separators, the LAST one is the decimal separator.
 * - A single comma is a decimal separator when followed by 1-2 digits,
 *   otherwise a thousands separator (money has at most 2 decimals).
 * - Sign is preserved; callers decide their own sign policy.
 *
 * Returns null when the value cannot be parsed as a number.
 */
export function parseMoneyAmount(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value !== 'string') return null

  let cleaned = value.trim()
  if (cleaned === '') return null

  // Strip currency tokens and symbols anywhere in the string
  cleaned = cleaned
    .replace(/\b(kr|sek|usd|eur)\b/gi, '')
    .replace(/[£€₹]|\$/g, '')
    .trim()

  // Remove spaces and non-breaking spaces (thousand separators)
  cleaned = cleaned.replace(/[\s  ]/g, '')

  const hasComma = cleaned.includes(',')
  const hasDot = cleaned.includes('.')

  if (hasComma && hasDot) {
    if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
      // "1.234,56": dots are thousands, comma is decimal
      cleaned = cleaned.replace(/\./g, '').replace(',', '.')
    } else {
      // "1,234.56": commas are thousands, dot is decimal
      cleaned = cleaned.replace(/,/g, '')
    }
  } else if (hasComma) {
    const commaCount = (cleaned.match(/,/g) || []).length
    const afterComma = cleaned.slice(cleaned.lastIndexOf(',') + 1)
    if (commaCount === 1 && afterComma.length >= 1 && afterComma.length <= 2) {
      // "12,5" / "40,50": decimal comma
      cleaned = cleaned.replace(',', '.')
    } else {
      // "1,234" / "12,345,678": thousands commas
      cleaned = cleaned.replace(/,/g, '')
    }
  }

  // Drop anything that still isn't part of a number
  cleaned = cleaned.replace(/[^0-9.-]/g, '')
  if (!/\d/.test(cleaned)) return null

  const parsed = parseFloat(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}
